import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { Label } from "@/dentlab/src/components/ui/label";

interface AkontoSelectionDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	availableAkonto: number;
	invoiceValue: number;
	currency?: string;
	onConfirm: (selectedAmount: number) => void;
}

const formatCurrency = (amount: number, currency = "CHF") => {
	return `${amount.toFixed(2)} ${currency}`;
};

export const AkontoSelectionDialog: React.FC<AkontoSelectionDialogProps> = ({
	open,
	onOpenChange,
	availableAkonto,
	invoiceValue,
	currency = "CHF",
	onConfirm,
}) => {
	const safeAvailableAkonto =
		typeof availableAkonto === "number" && !isNaN(availableAkonto)
			? availableAkonto
			: 0;
	const safeInvoiceValue =
		typeof invoiceValue === "number" && !isNaN(invoiceValue)
			? invoiceValue
			: 0;
	const maxAkonto = Math.min(safeAvailableAkonto, safeInvoiceValue);
	const [selectedAmount, setSelectedAmount] = useState<string>(
		maxAkonto.toFixed(2)
	);
	const [error, setError] = useState<string>("");

	useEffect(() => {
		if (open) {
			const defaultAmount = Math.min(
				safeAvailableAkonto,
				safeInvoiceValue
			);
			setSelectedAmount(defaultAmount.toFixed(2));
			setError("");
		}
	}, [open, safeAvailableAkonto, safeInvoiceValue]);

	const handleAmountChange = (value: string) => {
		setSelectedAmount(value);
		const numValue = parseFloat(value);

		if (isNaN(numValue) || numValue < 0) {
			setError("Bitte einen gültigen Betrag eingeben");
		} else if (numValue > safeAvailableAkonto) {
			setError(
				`Betrag kann nicht höher als verfügbares Akonto sein (${formatCurrency(
					safeAvailableAkonto,
					currency
				)})`
			);
		} else if (numValue > safeInvoiceValue) {
			setError(
				`Betrag kann nicht höher als Rechnungsbetrag sein (${formatCurrency(
					safeInvoiceValue,
					currency
				)})`
			);
		} else {
			setError("");
		}
	};

	const handleConfirm = () => {
		const numValue = parseFloat(selectedAmount);
		if (!isNaN(numValue) && numValue >= 0 && numValue <= maxAkonto) {
			onConfirm(numValue);
			onOpenChange(false);
		}
	};

	const handleNoAkonto = () => {
		onConfirm(0);
		onOpenChange(false);
	};

	const handleMaxAkonto = () => {
		setSelectedAmount(maxAkonto.toFixed(2));
		setError("");
	};

	const isValid = !error && selectedAmount !== "";

	return (
		<Dialog open={open} onOpenChange={onOpenChange} modal={true}>
			<DialogContent
				className="sm:max-w-md"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle>Akonto anwenden</DialogTitle>
					<DialogDescription>
						Wählen Sie, ob und wie viel Akonto auf diese Rechnung
						angewendet werden soll.
					</DialogDescription>
				</DialogHeader>
				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<div className="grid grid-cols-2 gap-2 text-sm">
							<div className="text-muted-foreground">
								Rechnungsbetrag:
							</div>
							<div className="font-medium text-right">
								{formatCurrency(safeInvoiceValue, currency)}
							</div>
							<div className="text-muted-foreground">
								Verfügbares Akonto:
							</div>
							<div className="font-medium text-right">
								{formatCurrency(safeAvailableAkonto, currency)}
							</div>
							<div className="text-muted-foreground">
								Max. anwendbar:
							</div>
							<div className="font-medium text-right">
								{formatCurrency(maxAkonto, currency)}
							</div>
						</div>
					</div>

					<div className="space-y-2">
						<Label htmlFor="akonto-amount">
							Anzuwendendes Akonto
						</Label>
						<div className="flex gap-2">
							<Input
								id="akonto-amount"
								type="number"
								step="0.01"
								min="0"
								max={maxAkonto}
								value={selectedAmount}
								onChange={(e) =>
									handleAmountChange(e.target.value)
								}
								className={error ? "border-red-500" : ""}
							/>
							<Button
								variant="outline"
								size="sm"
								onClick={handleMaxAkonto}
								disabled={
									parseFloat(selectedAmount) === maxAkonto
								}
							>
								Max
							</Button>
						</div>
						{error && (
							<p className="text-sm text-red-500">{error}</p>
						)}
					</div>

					{parseFloat(selectedAmount) > 0 && !error && (
						<div className="space-y-2 pt-2 border-t">
							<div className="grid grid-cols-2 gap-2 text-sm">
								<div className="text-muted-foreground">
									Verbleibt zu zahlen:
								</div>
								<div className="font-semibold text-right">
									{formatCurrency(
										safeInvoiceValue -
											parseFloat(selectedAmount),
										currency
									)}
								</div>
							</div>
						</div>
					)}
				</div>
				<DialogFooter className="flex-col sm:flex-row gap-2">
					<Button
						variant="outline"
						onClick={handleNoAkonto}
						className="w-full sm:w-auto"
						data-testid="skip-akonto-button"
						aria-label="Kein Akonto anwenden"
					>
						Kein Akonto anwenden
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={!isValid}
						className="w-full sm:w-auto"
						data-testid="confirm-akonto-button"
						aria-label="Akonto anwenden bestätigen"
					>
						Bestätigen
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
